import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, RefreshCw, TrendingUp, Wallet, Receipt, CircleDollarSign } from 'lucide-react';
import { formatCurrency, formatDateTime } from '../utils/salesUtils';
import { reportService } from '../api/reportService';

const MONTH_OPTIONS = [
  { value: 1, label: 'Jan' },
  { value: 2, label: 'Feb' },
  { value: 3, label: 'Mar' },
  { value: 4, label: 'Apr' },
  { value: 5, label: 'Mei' },
  { value: 6, label: 'Jun' },
  { value: 7, label: 'Jul' },
  { value: 8, label: 'Agu' },
  { value: 9, label: 'Sep' },
  { value: 10, label: 'Okt' },
  { value: 11, label: 'Nov' },
  { value: 12, label: 'Des' }
];

const toDateString = (year, month, day) => {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

const getLastDayOfMonth = (year, month) => {
  return new Date(year, month, 0).getDate();
};

export default function SalesDashboard() {
  const [searchQuery, setSearchQuery] = useState('');
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [startMonth, setStartMonth] = useState(1);
  const [endMonth, setEndMonth] = useState(currentMonth);
  const [pendingLimit, setPendingLimit] = useState(10);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [chartStartYear, setChartStartYear] = useState(currentYear - 4);
  const [chartEndYear, setChartEndYear] = useState(currentYear);

  const userData = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user_data") || "{}");
    } catch {
      return {};
    }
  }, []);
  const isAdmin = userData?.is_admin === true;

  const [appliedFilters, setAppliedFilters] = useState({
    selectedYear: currentYear,
    startMonth: 1,
    endMonth: currentMonth,
    pendingLimit: 10,
    chartStartYear: currentYear - 4,
    chartEndYear: currentYear
  });

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const rangeStart = toDateString(appliedFilters.selectedYear, appliedFilters.startMonth, 1);
      const rangeEnd = toDateString(
        appliedFilters.selectedYear,
        appliedFilters.endMonth,
        getLastDayOfMonth(appliedFilters.selectedYear, appliedFilters.endMonth)
      );

      const response = await reportService.getDashboard({
        start_date: rangeStart,
        end_date: rangeEnd,
        pending_limit: appliedFilters.pendingLimit,
        chart_start_year: appliedFilters.chartStartYear,
        chart_end_year: appliedFilters.chartEndYear
      });

      setDashboardData(response?.data || {});
    } catch (err) {
      setError(err?.message || 'Gagal memuat dashboard sales');
      setDashboardData(null);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const pendingRows = useMemo(() => {
    const rows = dashboardData?.pending_list || [];
    const query = searchQuery.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) => {
      const transaction = String(row.transaction_no || '').toLowerCase();
      const customer = String(row.customer || '').toLowerCase();
      return transaction.includes(query) || customer.includes(query);
    });
  }, [dashboardData, searchQuery]);

  const summary = dashboardData?.summary || {};
  const transactionStats = dashboardData?.transaction_stats || {};
  const pendingSummary = dashboardData?.pending_summary || {};
  const financialChart = dashboardData?.financial_chart || [];

  const chartMax = useMemo(() => {
    if (!financialChart.length) return 0;
    return Math.max(
      ...financialChart.flatMap((item) => [
        Number(item.income || 0),
        Number(item.procurement || 0),
        Number(item.expense || 0)
      ])
    );
  }, [financialChart]);

  const handleApplyFilters = () => {
    if (startMonth > endMonth) {
      setError('Bulan awal tidak boleh lebih besar dari bulan akhir.');
      return;
    }

    if (chartStartYear > chartEndYear) {
      setError('Chart Start Year tidak boleh lebih besar dari Chart End Year.');
      return;
    }

    setAppliedFilters({
      selectedYear,
      startMonth,
      endMonth,
      pendingLimit,
      chartStartYear,
      chartEndYear
    });
  };

  const StatCard = ({ icon: Icon, title, value }) => (
    <div className="bg-white rounded-lg shadow-sm border p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
        <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
          <Icon className="w-6 h-6 text-teal-600" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard Penjualan</h1>
          <p className="text-gray-600">Ringkasan KPI, pending, dan tren keuangan</p>
        </div>
        <div className="flex gap-3">
          <input
            type="number"
            min={2000}
            max={2100}
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value) || currentYear)}
            className="w-24 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
            title="Tahun"
          />

          <select
            value={startMonth}
            onChange={(e) => setStartMonth(Number(e.target.value))}
            className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
            title="Bulan awal"
          >
            {MONTH_OPTIONS.map((month) => (
              <option key={`start-${month.value}`} value={month.value}>{month.label}</option>
            ))}
          </select>

          <select
            value={endMonth}
            onChange={(e) => setEndMonth(Number(e.target.value))}
            className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
            title="Bulan akhir"
          >
            {MONTH_OPTIONS.map((month) => (
              <option key={`end-${month.value}`} value={month.value}>{month.label}</option>
            ))}
          </select>

          <input
            type="number"
            min={1}
            max={100}
            value={pendingLimit}
            onChange={(e) => setPendingLimit(Math.max(1, Math.min(100, Number(e.target.value) || 10)))}
            className="w-24 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
            title="Pending limit"
          />

          <input
            type="number"
            min={2000}
            max={chartEndYear}
            value={chartStartYear}
            onChange={(e) => setChartStartYear(Number(e.target.value) || currentYear - 4)}
            className="w-28 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
            title="Chart start year"
          />

          <input
            type="number"
            min={chartStartYear}
            max={2100}
            value={chartEndYear}
            onChange={(e) => setChartEndYear(Number(e.target.value) || currentYear)}
            className="w-28 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
            title="Chart end year"
          />

          <button
            onClick={handleApplyFilters}
            disabled={loading}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-2 disabled:opacity-60"
          >
            Terapkan
          </button>

          <button
            onClick={fetchDashboard}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-60"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="text-xs text-gray-500">
        Filter aktif: {MONTH_OPTIONS.find((m) => m.value === appliedFilters.startMonth)?.label} - {MONTH_OPTIONS.find((m) => m.value === appliedFilters.endMonth)?.label} {appliedFilters.selectedYear} | Chart: {appliedFilters.chartStartYear} - {appliedFilters.chartEndYear} | Pending limit: {appliedFilters.pendingLimit}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg p-4">{error}</div>
      )}

      {/* Statistics Cards */}
      {dashboardData && isAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            icon={CircleDollarSign}
            title="Total Penjualan"
            value={formatCurrency(summary.total_sales)}
          />
          <StatCard
            icon={Receipt}
            title="Total HPP"
            value={formatCurrency(summary.total_hpp)}
          />
          <StatCard
            icon={Wallet}
            title="Total Pengeluaran"
            value={formatCurrency(summary.total_expense)}
          />
          <StatCard
            icon={TrendingUp}
            title="Total Keuntungan"
            value={formatCurrency(summary.total_profit)}
          />
        </div>
      )}

      {/* Quick Blocks */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Produk Terlaris</h3>
          <div className="space-y-3">
            {(dashboardData?.top_products || []).map((product, index) => (
              <div key={`${product.product_name}-${index}`} className="flex items-center justify-between">
                <p className="text-sm text-gray-700 truncate pr-2">{product.product_name}</p>
                <p className="text-sm text-gray-600">{product.quantity} / {formatCurrency(product.total_sales)}</p>
              </div>
            ))}
            {(dashboardData?.top_products || []).length === 0 && <p className="text-sm text-gray-400">Tidak ada data</p>}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Kategori Terlaris</h3>
          <div className="space-y-3">
            {(dashboardData?.top_categories || []).map((category, index) => (
              <div key={`${category.category_name}-${index}`} className="flex items-center justify-between">
                <p className="text-sm text-gray-700 truncate pr-2">{category.category_name}</p>
                <p className="text-sm text-gray-600">{formatCurrency(category.total_sales)}</p>
              </div>
            ))}
            {(dashboardData?.top_categories || []).length === 0 && <p className="text-sm text-gray-400">Tidak ada data</p>}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Metode Pembayaran</h3>
          <div className="space-y-3">
            {(dashboardData?.payment_methods || []).map((item, index) => (
              <div key={`${item.payment_method}-${index}`} className="flex items-center justify-between">
                <p className="text-sm text-gray-700">{item.payment_method}</p>
                <p className="text-sm text-gray-600">{item.transaction_qty} trx</p>
              </div>
            ))}
            {(dashboardData?.payment_methods || []).length === 0 && <p className="text-sm text-gray-400">Tidak ada data</p>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Statistik Transaksi</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded border p-3 text-sm">Total: <strong>{transactionStats.total || 0}</strong></div>
            <div className="rounded border p-3 text-sm">Done: <strong>{transactionStats.done || 0}</strong></div>
            <div className="rounded border p-3 text-sm">Pending: <strong>{transactionStats.pending || 0}</strong></div>
            <div className="rounded border p-3 text-sm">Cancelled: <strong>{transactionStats.cancelled || 0}</strong></div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Pending Summary</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded border p-3 text-sm">Total Nominal: <strong>{formatCurrency(pendingSummary.total_amount || 0)}</strong></div>
            <div className="rounded border p-3 text-sm">Qty Transaksi: <strong>{pendingSummary.transaction_qty || 0}</strong></div>
          </div>
        </div>
      </div>

      {isAdmin && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Grafik Keuangan Tahunan</h3>
          <div className="space-y-3 mb-5">
            {financialChart.map((item) => {
              const income = Number(item.income || 0);
              const procurement = Number(item.procurement || 0);
              const expense = Number(item.expense || 0);
              const incomeWidth = chartMax > 0 ? `${(income / chartMax) * 100}%` : '0%';
              const procurementWidth = chartMax > 0 ? `${(procurement / chartMax) * 100}%` : '0%';
              const expenseWidth = chartMax > 0 ? `${(expense / chartMax) * 100}%` : '0%';

              return (
                <div key={`bar-${item.year}`}>
                  <div className="text-xs font-semibold text-gray-600 mb-1">{item.year}</div>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="w-20 text-[11px] text-gray-500">Pemasukan</span>
                      <div className="h-2.5 bg-gray-100 rounded w-full overflow-hidden">
                        <div className="h-2.5 bg-green-500" style={{ width: incomeWidth }} />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-20 text-[11px] text-gray-500">Pengadaan</span>
                      <div className="h-2.5 bg-gray-100 rounded w-full overflow-hidden">
                        <div className="h-2.5 bg-blue-500" style={{ width: procurementWidth }} />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-20 text-[11px] text-gray-500">Pengeluaran</span>
                      <div className="h-2.5 bg-gray-100 rounded w-full overflow-hidden">
                        <div className="h-2.5 bg-amber-500" style={{ width: expenseWidth }} />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tahun</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pemasukan</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pengadaan</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pengeluaran</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {financialChart.map((item) => (
                  <tr key={item.year}>
                    <td className="px-4 py-3">{item.year}</td>
                    <td className="px-4 py-3">{formatCurrency(item.income)}</td>
                    <td className="px-4 py-3">{formatCurrency(item.procurement)}</td>
                    <td className="px-4 py-3">{formatCurrency(item.expense)}</td>
                  </tr>
                ))}
                {financialChart.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-4 text-center text-gray-400">Tidak ada data grafik</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pending Search */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Cari transaksi pending..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center justify-end">
            <button
              onClick={fetchDashboard}
              disabled={loading}
              className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-2 disabled:opacity-60"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh Data
            </button>
          </div>
        </div>
      </div>

      {/* Pending Transactions Table */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">Transaksi Pending</h3>
            <div className="text-sm text-gray-500">Total: {pendingRows.length} transaksi</div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  No. Transaksi
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pelanggan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tanggal Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Grand Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status Bayar
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status Transaksi
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-red-500">
                    Error: {error}
                  </td>
                </tr>
              ) : pendingRows.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                    Tidak ada data pending
                  </td>
                </tr>
              ) : (
                pendingRows.map((order) => {
                  return (
                    <tr key={order.order_id || order.transaction_no} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {order.transaction_no}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {order.customer || 'Pelanggan Umum'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDateTime(order.order_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-teal-100 text-teal-700">
                          {formatCurrency(order.grand_total)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-700">
                          {order.payment_status || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-700">
                          {order.transaction_status || 'PENDING'}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}